import { getAccessToken } from '../authentication/supabaseService';

// Thrown when the server rejects a create/update because the article ID already
// exists for this supplier (UNIQUE(restaurant_id, article_id, platform,
// custom_platform_name), 409).
export class CustomIngredientDuplicateArticleApiError extends Error {
  constructor() {
    super('An ingredient with this article ID already exists for this supplier');
    this.name = 'CustomIngredientDuplicateArticleApiError';
  }
}

// Map a failed create/update response to a typed/duplicate error, else surface the
// server's error message when present so callers show something more useful than a status.
async function parseCustomIngredientError(res: Response, fallback: string): Promise<Error> {
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    /* no JSON body */
  }
  if (body?.error === 'duplicate_article') {
    return new CustomIngredientDuplicateArticleApiError();
  }
  const serverMessage = typeof body?.error === 'string'
    ? body.error
    : (typeof body?.message === 'string' ? body.message : null);
  return new Error(serverMessage || fallback);
}

export interface CustomIngredient {
  id: string;
  restaurantId: string;
  articleId: string;
  productName: string;
  price: number;
  priceWithTaxes: number;
  taxRate: number;
  platform: string;
  customPlatformName?: string;
  // Optional mid-level category (same enum as order products). Drives the inventory
  // category label in the Cloud Count app instead of the generic "custom" badge.
  category?: string;
  quantityInfo: {
    stdAmount: number;
    stdMultiplier: number;
    stdUnit: string;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface CustomIngredientCreateRequest {
  restaurantId: string;
  articleId: string;
  productName: string;
  price: number;
  priceWithTaxes: number;
  taxRate: number;
  platform: string;
  customPlatformName?: string;
  category?: string | null;
  quantityInfo: {
    stdAmount: number;
    stdMultiplier: number;
    stdUnit: string;
  };
}

export interface CustomIngredientUpdateRequest {
  id: string;
  articleId?: string;
  productName?: string;
  price?: number;
  priceWithTaxes?: number;
  taxRate?: number;
  platform?: string;
  customPlatformName?: string;
  category?: string | null;
  quantityInfo?: {
    stdAmount: number;
    stdMultiplier: number;
    stdUnit: string;
  };
}

export async function fetchCustomIngredientsByRestaurant(
  restaurantId: string
): Promise<CustomIngredient[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/customingredients/${restaurantId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch custom ingredients: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return data.map((item: any) => ({
    ...item,
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.updatedAt)
  }));
}

export async function fetchCustomIngredientById(id: string): Promise<CustomIngredient> {
  const token = await getAccessToken();
  const res = await fetch(`/api/customingredients/ingredient/${id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch custom ingredient: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt)
  };
}

export async function createCustomIngredient(
  request: CustomIngredientCreateRequest
): Promise<CustomIngredient> {
  const token = await getAccessToken();
  const res = await fetch(`/api/customingredients/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request)
  });
  if (!res.ok) {
    throw await parseCustomIngredientError(res, `Failed to create custom ingredient: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt)
  };
}

export async function updateCustomIngredient(
  request: CustomIngredientUpdateRequest
): Promise<CustomIngredient> {
  const token = await getAccessToken();
  const res = await fetch(`/api/customingredients/update`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request)
  });
  if (!res.ok) {
    throw await parseCustomIngredientError(res, `Failed to update custom ingredient: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt)
  };
}

// ---- Supplier price file import (batch) ----

export interface ExtractedPriceRow {
  productName: string;
  articleId?: string;
  price: number;
  weightString?: string;
  quantityInfo: {
    stdAmount: number;
    stdMultiplier: number;
    stdUnit: string;
  };
}

// A row parsed from OUR custom-ingredient template (deterministic, no AI). Carries
// its own supplier / VAT / category because one template can hold many suppliers.
export interface TemplateIngredientRow extends ExtractedPriceRow {
  rowNumber: number;
  supplierName: string;
  taxRate: number;
  priceWithTaxes: number;
  category?: string;
  // Problems found while parsing; the review table blocks saving until they're fixed.
  errors: string[];
}

// A reviewed row ready for bulk upsert: the extracted ex-VAT row plus the
// incl-VAT price and rate derived from the file-wide VAT selection. Template rows
// additionally carry their own supplier + category.
export interface BulkCustomIngredientRow extends ExtractedPriceRow {
  priceWithTaxes: number;
  taxRate: number;
  supplierName?: string;
  category?: string;
}

export interface BulkUpsertResult {
  created: number;
  updated: number;
  duplicatesMerged: number;
  skippedExisting: string[];
}

/**
 * Upload one or more supplier price files and get back extracted rows (not saved).
 */
export async function extractSupplierPriceFile(files: File[]): Promise<{ rows: ExtractedPriceRow[]; cost: number }> {
  const token = await getAccessToken();
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));

  const res = await fetch(`/api/customingredients/extractFromFile`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) {
    throw new Error(`Failed to extract supplier price file: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

/**
 * Upload a filled-in custom ingredient template and get back parsed rows (not saved).
 * `totalRows` can exceed `rows.length` when the file is longer than the parser's cap.
 */
export async function parseCustomIngredientTemplate(
  file: File
): Promise<{ rows: TemplateIngredientRow[]; totalRows: number }> {
  const token = await getAccessToken();
  const formData = new FormData();
  formData.append('files', file);

  const res = await fetch(`/api/customingredients/parseTemplate`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) {
    throw await parseCustomIngredientError(res, `Failed to read the template: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

/**
 * Bulk create/update custom ingredients from reviewed price-file or template rows.
 * `skipExisting` (template import) leaves already-existing ingredients untouched
 * and reports them instead of overwriting their price.
 */
export async function bulkCreateCustomIngredients(
  restaurantId: string,
  supplierName: string,
  rows: BulkCustomIngredientRow[],
  skipExisting = false
): Promise<BulkUpsertResult> {
  const token = await getAccessToken();
  const res = await fetch(`/api/customingredients/bulkCreate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ restaurantId, supplierName, rows, skipExisting }),
  });
  if (!res.ok) {
    throw new Error(`Failed to bulk create custom ingredients: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

export async function deleteCustomIngredient(id: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/customingredients/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to delete custom ingredient: ${res.status} ${res.statusText}`);
  }
}